import { CommandResult, FormApiService, ItemCommandResult, QueryApiService } from 'src/common/api-service';
import { AxiosError } from 'axios';
import { DraftShortlistDto } from './types';

export interface SendDemoShortlistOtpInput {
  email: string;
  linkedInJobPostUrl: string;
}

export interface SendDemoShortlistOtpResult {
  otpRequestId: string;
  otpSkipped: boolean;
}

export interface VerifyDemoShortlistOtpInput {
  otpRequestId: string;
  otp: string;
}

export interface BackgroundWorkInfo {
  jobId: string;
}

/**
 * The public read of a draft shortlist, and only that.
 *
 * The OData read-by-key of `draftShortlists` is `Backoffice`-only: it filters by key alone and `$expand`
 * walks from there to the draft enterprise profile and its admin's name, e-mail and phone (j2l-website#1893).
 * This service used to carry a `getDraftShortlistOdata` with exactly that expand in its defaults, uncalled;
 * it is gone. The inherited `getById` / `getWithOData` are still public, so they still reach that route and
 * will answer 403 for every role this SPA can hold. What the website is meant to use is below: `{id}/public`,
 * which is anonymous by design and returns a fixed DTO with no admin data in it.
 */
export class DraftShortlistApiService extends QueryApiService<any> {
  constructor() {
    super('draftShortlists');
  }

  public async getDraftShortlist(id: string): Promise<DraftShortlistDto> {
    return await this.get<DraftShortlistDto>(`${encodeURIComponent(id)}/public`);
  }
}

export class DraftShortlistFormApiService extends FormApiService {
  constructor() {
    super('draftShortlists');
  }

  public async visit(id: string): Promise<CommandResult> {
    try {
      return await this.put<any, CommandResult>({}, `${encodeURIComponent(id)}/visit`);
    } catch (e) {
      const axiosError = e as AxiosError<CommandResult>;
      if (!axiosError?.response?.data) throw e;
      return axiosError.response.data;
    }
  }

  public async registerEnterprise(enterpriseProfileId: string, draftShortlistId: string): Promise<CommandResult> {
    try {
      return await this.post<{ draftShortlistId: string }, CommandResult>(
        { draftShortlistId },
        `${encodeURIComponent(enterpriseProfileId)}/register`
      );
    } catch (e) {
      const axiosError = e as AxiosError<CommandResult>;
      if (!axiosError?.response?.data) throw e;
      return axiosError.response.data;
    }
  }

  public async saveDemoContact(body: { email: string; jobPostUrl: string }): Promise<CommandResult> {
    try {
      return await this.post<{ email: string; jobPostUrl: string }, CommandResult>(body, 'demo/contact');
    } catch (e) {
      const axiosError = e as AxiosError<CommandResult>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data as CommandResult;
    }
  }

  public async sendDemoOtp(body: SendDemoShortlistOtpInput): Promise<ItemCommandResult<SendDemoShortlistOtpResult>> {
    try {
      return await this.post<SendDemoShortlistOtpInput, ItemCommandResult<SendDemoShortlistOtpResult>>(body, 'demo/sendOtp');
    } catch (e) {
      const axiosError = e as AxiosError<CommandResult>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data as any;
    }
  }

  public async verifyDemoOtp(body: VerifyDemoShortlistOtpInput): Promise<ItemCommandResult<BackgroundWorkInfo>> {
    try {
      return await this.post<VerifyDemoShortlistOtpInput, ItemCommandResult<BackgroundWorkInfo>>(body, 'demo/verifyOtp');
    } catch (e) {
      const axiosError = e as AxiosError<CommandResult>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data as any;
    }
  }
}
